<template>
    <div class="animated fadeIn">
        <b-card class="card-border mt-4">
            <b-card-title><i class="fa fa-exchange"></i> Transfer Company Request</b-card-title>
            <b-card-sub-title>Manage all transfer company requests from all companies</b-card-sub-title>
            <div fluid class="px-2 mt-4">
                <loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />

                <b-row class="mt-2">
                    <b-col sm="12" md="3" lg="3">
                        <b-button v-b-popover.hover.right="'Toggle to show/hide filter options'" v-b-toggle.collapse-1
                            class="filter">
                            FILTER OPTIONS
                        </b-button>
                    </b-col>
                    <b-col sm="12">
                        <!-- Collapsible Filter Options -->
                        <b-collapse id="collapse-1" class="mt-2">
                            <b-card>
                                <b-row no-gutters>
                                    <b-col lg="4" md="6" sm="12" class="mr-4">
                                        <b-form-group label="Date From">
                                            <b-form-datepicker v-model="filterBy.dateFrom" locale="en" reset-button
                                                label-reset-button="Clear" :date-format-options="{
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: '2-digit',
                                                    weekday: 'short',
                                                }" :date-disabled-fn="dateFromDisabled" />
                                        </b-form-group>
                                    </b-col>
                                    <b-col lg="4" md="6" sm="12" class="mr-4">
                                        <b-form-group label="Date To">
                                            <b-form-datepicker v-model="filterBy.dateTo" locale="en" reset-button
                                                label-reset-button="Clear" :date-format-options="{
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: '2-digit',
                                                    weekday: 'short',
                                                }" :date-disabled-fn="dateFromDisabled" />
                                        </b-form-group>
                                    </b-col>
                                    <b-col lg="4" md="6" sm="12" class="mr-4">
                                        <b-form-group label="Company">
                                            <v-select class="style-chooser" label="text"
                                                placeholder=" - Please select - " :options="companyOptions"
                                                :reduce="(company) => company.value" v-model="filterBy.company">
                                                <template v-slot:no-options="{ search, searching }">
                                                    <template v-if="searching">
                                                        No results found for
                                                        <em>
                                                            <strong>{{ search }}</strong>
                                                        </em>
                                                    </template>
                                                    <em :style="{ opacity: 0.5 }" v-else>
                                                        Start typing to search for a company
                                                    </em>
                                                </template>
                                            </v-select>
                                        </b-form-group>
                                    </b-col>
                                    <b-col lg="4" md="6" sm="12" class="mr-4">
                                        <b-form-group label="Status">
                                            <v-select class="style-chooser" label="text"
                                                placeholder=" - Please select - " :options="requestStatusOptions"
                                                :reduce="(status) => status.value" v-model="filterBy.status">

                                                <template v-slot:no-options="{ search, searching }">
                                                    <template v-if="searching">
                                                        No results found for
                                                        <em>
                                                            <strong>{{ search }}</strong>
                                                        </em>
                                                    </template>
                                                    <em :style="{ opacity: 0.5 }" v-else>
                                                        Start typing to search for status
                                                    </em>
                                                </template>
                                            </v-select>
                                        </b-form-group>
                                    </b-col>
                                    <b-col lg="4" md="6" sm="12" class="mr-4">
                                        <b-form-group label="Request Id">
                                            <b-form-input id="requestId" name="Request Id" type="search" class="numFont"
                                                v-model="filterBy.requestId"
                                                v-validate="{ regex: /^([a-zA-Z0-9\-])*$/ }" />
                                            <span v-show="errors.has('Request Id')" class="help-block">{{
                                                errors.first('Request Id')
                                                }}</span>
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                                <b-row no-gutters>
                                    <b-col sm="12">
                                        <b-button class="mr-1" variant="success" @click="onFilterRequest">
                                            Search
                                        </b-button>
                                        <b-button class="mr-1" variant="primary" @click="resetFilters">
                                            Reset
                                        </b-button>
                                    </b-col>
                                </b-row>
                            </b-card>
                        </b-collapse>
                    </b-col>
                </b-row>

                <!-- Select Actions and Items Per Page Options -->
                <b-row>
                    <b-col sm="6" md="3" class="mt-4 mb-2">
                        <b-dropdown text=" Select Actions " variant="dark" slot="append">
                            <b-dropdown-item @click="updateSelRequestsToConfirm" v-show="!isViewer">
                                Confirm Transfer Company Request(s)
                            </b-dropdown-item>
                            <b-dropdown-item>
                                <json-excel :data="exportData" :fields="exportFields" type="xls"
                                    :name="fileName + '.xls'">
                                    Export Transfer Company Requests in Excel
                                </json-excel>
                            </b-dropdown-item>
                            <b-dropdown-item>
                                <json-excel :data="exportData" :fields="exportFields" type="csv"
                                    :name="fileName + '.csv'">
                                    Export Transfer Company Requests to CSV
                                </json-excel>
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-col>
                    <b-col sm="6" md="4" offset-md="5" class="mt-4 mb-2 text-md-right">
                        <b-input-group prepend="Show" append="/ Page">
                            <b-form-select :options="pageOptions" v-model="perPage" />
                        </b-input-group>
                    </b-col>
                </b-row>

                <b-table ref="transferRequestsTable" show-empty striped hover :items="items" :fields="fields"
                    :current-page="currentPage" :per-page="perPage" :filter="filter" :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc" :sort-direction="sortDirection" responsive selectable
                    @row-clicked="rowClicked">

                    <template v-slot:cell(selected)="{ rowSelected }">
                        <template v-if="rowSelected && !isViewer">
                            <span aria-hidden="true">&check;</span>
                            <span class="sr-only">Selected</span>
                        </template>

                        <template v-else>
                            <span aria-hidden="true">&nbsp;</span>
                            <span class="sr-only">Not selected</span>
                        </template>
                    </template>

                    <template v-slot:cell(requestId)="row">
                        <span class="numFont">
                            {{ row.item.requestId }}
                        </span>
                    </template>

                    <template v-slot:cell(dateCreated)="row">{{
                        row.item.dateCreated
                            ? getDisplayDateTime(row.item.dateCreated)
                            : '-'
                    }}</template>

                    <template v-slot:cell(user)="row">
                        {{ row.item.user }}
                    </template>

                    <template v-slot:cell(currCompany)="row">
                        {{ row.item.currCompany }}
                        <br />
                        <span class="company-description">{{ getCompanyDescription(row.item.currCompanyId) }}</span>
                    </template>

                    <template v-slot:cell(newCompany)="row">
                        {{ row.item.newCompany }}
                        <br />
                        <span class="company-description">{{ getCompanyDescription(row.item.newCompanyId) }}</span>
                    </template>

                    <template v-slot:cell(status)="row">
                        <TransferRequestRowStatus :row="row" />
                    </template>

                    <template v-slot:cell(actions)="row">
                        <TransferRequestRowActions :row="row" :isSuperAdmin="isSuperAdmin" :isViewer="isViewer" />
                    </template>

                    <template slot="row-details" slot-scope="row">
                        <TransferRequestDetailsView :row="row" />
                    </template>
                </b-table>

                <b-row>
                    <b-col md="4" sm="12" class="my-1">
                        <b-button size="sm" @click="selectAllRows" v-show="!isViewer">Select all</b-button>&nbsp;&nbsp;
                        <b-button size="sm" @click="clearSelectedRows" v-show="!isViewer">Clear
                            selected</b-button>&nbsp;&nbsp;
                    </b-col>

                    <b-col md="4" sm="12" class="my-1">
                        <span class="total-display">Total: {{ totalRows ? totalRows.toLocaleString() : 0 }}</span>
                        &nbsp;&nbsp;|&nbsp;&nbsp;
                        <span class="total-display" v-show="!isViewer">
                            Selected: {{ Object.keys(selected).length }}
                        </span>
                    </b-col>

                    <b-col md="4" sm="12" class="my-1">
                        <b-pagination align="right" :total-rows="totalRows" :per-page="perPage" v-model="currentPage"
                            class="my-0" />
                    </b-col>
                </b-row>

            </div>
        </b-card>

        <!-- Modals here -->
        <ConfirmTransferRequest :allCompaniesObj="allCompaniesObj" />
        <ConfirmTransferRequests :allCompaniesObj="allCompaniesObj" />
    </div>
</template>

<script>
// Components
import ConfirmTransferRequest from '@/views/support/transferRequest/ConfirmTransferRequest';
import ConfirmTransferRequests from '@/views/support/transferRequest/ConfirmTransferRequests';
import TransferRequestRowStatus from '@/views/support/transferRequest/TransferRequestRowStatus';
import TransferRequestRowActions from '@/views/support/transferRequest/TransferRequestRowActions';
import TransferRequestDetailsView from '@/views/support/transferRequest/TransferRequestDetailsView';

// Util
import { DateUtil } from '@/utils/dateutil';
import { DropDownItemsUtil } from '@/utils/dropDownItemsUtil';
import { ValidationUtil } from '@/utils/validationUtil';
import { UserUtil } from '@/utils/userutil';

// API
import requestApi from '@/api/requestApi';

// DAO
import requestDAO from '@/database/transferCompanyRequests';

// Others
import config from '@/config/env-constants';
import EventBus from '@/shared/event-bus';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import JsonExcel from 'vue-json-excel';
import moment from 'moment';
import _ from 'lodash';

export default {
    name: 'transfer-company-requests',
    components: {
        ConfirmTransferRequest,
        ConfirmTransferRequests,
        TransferRequestRowStatus,
        TransferRequestRowActions,
        TransferRequestDetailsView,
        JsonExcel,
        Loading,
    },
    data() {
        return {
            items: [],
            fields: [
                {
                    key: 'selected',
                    label: '#',
                    sortable: false,
                },
                {
                    key: 'requestId',
                    sortable: true,
                },
                {
                    key: 'dateCreated',
                    sortable: true,
                },
                {
                    key: 'user',
                    sortable: true,
                },
                {
                    key: 'currCompany',
                    label: "Current Company",
                    sortable: true,
                },
                {
                    key: 'newCompany',
                    sortable: true,
                },
                'status',
                {
                    key: 'actions',
                    thClass: 'text-center'
                }
            ],
            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            pageOptions: [5, 10, 15, 25, 50, 100],
            sortBy: null,
            sortDesc: false,
            sortDirection: 'asc',
            filter: null,

            // array of selecteds from table
            selected: [],

            companyOptions: [],
            requestStatusOptions: config.requestStatusOptions,

            allCompaniesObj: {},
            allTransferRequestsObj: {},

            defaultFilterBy: {
                dateFrom: moment().format('YYYY-MM-DD'),
                dateTo: moment().format('YYYY-MM-DD'),
                company: { ...config.companyDefaultValue },
                status: null,
                requestId: ''
            },
            filterBy: {
                dateFrom: moment().format('YYYY-MM-DD'),
                dateTo: moment().format('YYYY-MM-DD'),
                company: { ...config.companyDefaultValue },
                status: null,
                requestId: ''
            },
            prevFilter: {},

            isSuperAdmin: this.$store.getters.isSuperAdmin,
            isViewer: this.$store.getters.isViewer,
            loggedUser: this.$store.getters.loggedUser,
            loggedUserCompany: this.$store.getters.loggedUserCompany,

            // Check for loader
            isLoading: false,

            // Listener
            requestListener: null,
        };
    },
    computed: {
        /**
         * Returns the set of data to be included in the export. For now this just
         * returns the data as is.
         *
         * @returns {Array} the set of data to be included in the export.
         */
        exportData() {
            return this.items;
        },

        /**
         * Derives the field information based from the data table configuration.
         *
         * @returns {object} the fields to be included in the export.
         */
        exportFields() {
            return {
                "Request ID": 'requestId',
                "First Name": 'firstName',
                "Last Name": 'lastName',
                "Current Company": 'currCompany',
                "New Company": 'newCompany',
                Status: 'status',
            };
        },

        fileName() {
            let currTimeStamp = DateUtil.getCurrentTimestamp();
            return 'TransferCompanyRequests-' + DateUtil.getDateInDDMMYYYYHHSSFormat(currTimeStamp);
        },
    },
    watch: {
        items(value) {
            this.$nextTick(() => {
                // Apply existing highlighting for selected rows
                let selectedItems = _.intersectionBy(value, this.selected, 'id');

                selectedItems.forEach((item) => {
                    // retrieve index with highlights
                    let index = value.indexOf(item);
                    // apply hightlights programmatically
                    this.$refs.transferRequestsTable.selectRow(index);
                });
            });
        },
    },
    mounted() {
        setTimeout(async () => {
            try {
                // Filter Access
                if (this.$store.getters.isViewer || this.$store.getters.isScanner) {
                    this.$router.push('/dashboard');
                    this.$toaster.warning('You are not allowed to access this page.');
                }

                // Show loader
                this.isLoading = true;

                // Load initial data
                this.allCompaniesObj = { ...this.$store.getters.companies };
                this.companyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);

                await this.resetFilters();
            } catch (_error) {
                this.$toaster.error('Error loading data. Please reload the page again.');
            } finally {
                // hide loading indicator
                this.isLoading = false;
            }
        }, config.timeout);

        // Event Listeners
        EventBus.$on('onConfirmTransferRequest', (transferRequestObj) => {
            if (!_.isEmpty(transferRequestObj)) {
                this.allTransferRequestsObj[transferRequestObj.id] = transferRequestObj;
                this.filterTableContent();
                this.clearSelectedRows();
            }
        });
        EventBus.$on('onConfirmTransferRequests', (transferRequestsObj) => {
            if (!_.isEmpty(transferRequestsObj)) {
                _.forEach(transferRequestsObj, transferRequestObj => {
                    this.allTransferRequestsObj[transferRequestObj.id] = transferRequestObj;
                })
                this.filterTableContent();
                this.clearSelectedRows();
            }
        });
    },

    methods: {
        getCompanyDescription(companyId) {
            let companyObj = this.allCompaniesObj[companyId];
            if (companyObj && !_.isEmpty(companyObj) && companyObj.description) {
                return companyObj.description;
            }
            return "-"
        },

        listenerCallback(type, request) {
            if ((!this.allTransferRequestsObj[request.id] && type === "added") || type === "modified") {
                this.allTransferRequestsObj[request.id] = request;
                this.filterTableContent();
            }
        },

        filterTableContent() {
            let filteredObj = { ...this.allTransferRequestsObj };

            _.forEach(this.allTransferRequestsObj, (request, id) => {
                this.filterByCompany(filteredObj, request, id);

                if (this.filterBy.status !== null &&
                    this.filterBy.status !== request.status) {
                    delete filteredObj[id];
                }

                let requestId = this.filterBy.requestId;
                if (
                    requestId &&
                    requestId.length > 0 &&
                    requestId !== request.requestId
                ) {
                    delete filteredObj[id];
                }
            });

            this.processTransferRequests(filteredObj);
        },
        processTransferRequests(transferRequests) {
            this.items = Object.values(transferRequests);

            _.forEach(this.items, item => {
                item.user = item.firstName + ' ' + item.lastName;

                // remove show details
                delete item._showDetails;
            });
            this.items = _.sortBy(this.items, ['dateCreated']);
            this.items.reverse();
            this.totalRows = this.items.length;

            // refresh table
            if (this.$refs.transferRequestsTable) {
                this.$refs.transferRequestsTable.refresh();
            }
        },
        filterByCompany(filteredObjs, request, id) {
            let companyIdFilter = this.filterBy.company.id;
            let companyId = request.currCompanyId;
            if ((companyIdFilter && companyIdFilter !== companyId) || !UserUtil.hasCompanyAccess(this.allCompaniesObj, companyId)) {
                delete filteredObjs[id];
            }
        },

        dateFromDisabled(_ymd, date) {
            return date > new Date();
        },
        validateFilter() {
            let isValid = true;

            if (this.filterBy.dateFrom > this.filterBy.dateTo) {
                this.$toaster.warning(
                    'Invalid Date Range. Date From must be less than Date To.'
                );
                isValid = false;
            } else if (!ValidationUtil.isAlphaNumeric(this.filterBy.requestId)) {
                this.$toaster.warning(
                    'Invalid Request ID. Please enter a valid Request ID'
                );
                isValid = false;
            }

            return isValid;
        },

        async onFilterRequest() {
            if (!this.validateFilter()) {
                return;
            }

            if (!_.isEqual(this.filterBy, this.prevFilter)) {
                await this.retrieveData();
                this.prevFilter = { ...this.filterBy };
            }

            // clear selected rows
            this.clearSelectedRows();
        },
        async resetFilters() {
            if (!_.isEqual(this.filterBy, this.defaultFilterBy)) {
                // reset to default
                this.filterBy = { ...this.defaultFilterBy };
                this.prevFilter = { ...this.filterBy };

                // reset validation
                this.$validator.reset();
                this.errors.clear();

                await this.retrieveData();
            }

            // clear selected rows
            this.clearSelectedRows();
        },
        async retrieveData() {
            try {
                // Show loader
                this.isLoading = true;

                let filter = { ...this.filterBy };
                filter.companyId = this.loggedUserCompany.id;
                filter.fromTimestamp = DateUtil.startDateTimeStamp(
                    new Date(filter.dateFrom)
                );
                filter.toTimestamp = DateUtil.endDateTimeStamp(new Date(filter.dateTo));

                if (this.isSuperAdmin) {
                    const { data } = await requestApi.getTransferCompanyRequests(
                        filter,
                        config.view.ADMIN,
                        this.loggedUser.id
                    );
                    this.allTransferRequestsObj = _.assign(
                        this.allTransferRequestsObj,
                        data.transferRequests
                    );
                } else {
                    const { data } = await requestApi.getTransferCompanyRequests(
                        filter,
                        config.view.COMPANY,
                        this.loggedUser.id
                    );
                    this.allTransferRequestsObj = _.assign(
                        this.allTransferRequestsObj,
                        data.transferRequests
                    );
                }

                // update listener
                this.requestListener = requestDAO.getTransferCompanyRequestListener(filter, this.listenerCallback);

                this.filterTableContent();
            } catch (_error) {
                this.$toaster.error('Error loading data. Please reload the page again.');
            } finally {
                // hide loading indicator
                this.isLoading = false;
            }
        },

        // ROW SELECTION
        updateSelRequestsToConfirm() {
            if (this.selected.length === 0) {
                this.$toaster.warning('Please select at least 1 "Open" transfer company request.');
                return;
            } else if (
                _.findIndex(this.selected, { status: 'Open' }) < 0
            ) {
                this.$toaster.warning(
                    'Please select at least one "Open" transfer company request.'
                );
                return;
            }

            this.$bvModal.show('confirm-transfer-requests');
            EventBus.$emit('onUpdateSelRequestsToConfirm', this.selected);
        },
        rowClicked(item) {
            if (_.findIndex(this.selected, { id: item.id }) < 0) {
                this.selected.push(item);
            } else {
                for (let i = 0; i < this.selected.length; i++) {
                    if (this.selected[i].id === item.id) {
                        this.selected.splice(i, 1);
                        break;
                    }
                }
            }
        },
        selectAllRows() {
            this.$refs.transferRequestsTable.selectAllRows();
            this.items.forEach((item) => {
                if (_.findIndex(this.selected, { id: item.id }) < 0) {
                    this.selected.push(item);
                }
            });
        },
        clearSelectedRows() {
            this.$refs.transferRequestsTable.clearSelected();

            // clear selected rows
            this.selected = [];
        },

        // UTILS
        getDisplayDateTime(date) {
            return DateUtil.getFormattedDateWithTime(date);
        },
    },

    beforeUnmount() {
        if (this.requestListener != null) {
            // Stop listening to changes
            this.requestListener();
        }
    },

    beforeDestroy() {
        EventBus.$off('onConfirmTransferRequest');
        EventBus.$off('onConfirmTransferRequests');
    },
};
</script>

<style scoped>
.company-description {
    font-size: 11px;
}
</style>